/**
 * ----------------------------------------------------------------------------------------------------------------------
 * KACHING VARIANT BRIDGE
 * ----------------------------------------------------------------------------------------------------------------------
 *
 * Impact's product gallery only reacts to the "variant:change" event, which is emitted by the theme's own
 * <variant-picker> element (see the ProductGallery class in theme.js). Most of our product templates do not include the
 * variant_picker block at all, and on the ones that do, Kaching Bundles hides it (hideVariantPicker: true in the app
 * block settings). The colour is therefore picked in Kaching's own selector, which the theme never hears about, so the
 * gallery keeps showing the image of the variant that happened to be selected when the page was rendered.
 *
 * Kaching does update the product form's variant input (input[name="id"]) so that the native add-to-cart keeps working.
 * This bridge watches that input and re-emits the theme's own "variant:change" event on the form, which is all the
 * gallery needs to scroll to the right image. Nothing else in the theme listens to that event, so this cannot cascade.
 *
 * The bridge is deliberately app-agnostic: it observes the value of the variant input rather than Kaching's markup, so
 * it keeps working when the app updates its selector, and it stays inert on pages where nothing touches the input.
 *
 * ********************************************
 * How a value change is detected
 * ********************************************
 *
 * A hidden input can be written in several ways and only some of them are observable, so all of them are covered:
 *
 *   * input.value = "…"          -> intercepted through a per-element accessor that wraps the native one
 *   * input.setAttribute("value") -> caught by a MutationObserver on the attribute
 *   * a "change"/"input" event    -> caught by a delegated listener
 *   * the native setter called directly (input node replaced, framework-style writes) -> caught by the poll below
 *
 * The poll is the safety net for writes that bypass the accessor entirely. It compares a handful of strings a few times
 * per second and pauses while the tab is hidden, so its cost is irrelevant next to the certainty it buys.
 *
 * ********************************************
 * Duplicate events
 * ********************************************
 *
 * On templates that still render the theme's own <variant-picker>, the picker sets the input value and then dispatches
 * "variant:change" itself, synchronously. The bridge defers its own dispatch to a microtask and records every
 * "variant:change" it sees on the form, so the picker's event always wins and no duplicate is emitted.
 *
 * Add ?kaching_bridge_debug to a product URL to log what the bridge sees in the console.
 */

(() => {
  const ELEMENT_NAME = 'kaching-variant-bridge';

  if (window.customElements.get(ELEMENT_NAME)) {
    return;
  }

  const CART_FORM_SELECTOR = 'form[action*="/cart/add"]';
  const VARIANT_INPUT_SELECTOR = 'input[name="id"]';
  const POLL_INTERVAL = 200;

  const nativeValueDescriptor = Object.getOwnPropertyDescriptor(HTMLInputElement.prototype, 'value');

  class KachingVariantBridge extends HTMLElement {
    connectedCallback() {
      this._variants = new Map();

      this._mediaFilters = new Map();

      try {
        const variants = JSON.parse(this.querySelector('script[type="application/json"]:not([data-media-filters])')?.textContent || '[]');
        variants.forEach((variant) => this._variants.set(String(variant.id), variant));

        const mediaFilters = JSON.parse(this.querySelector('script[data-media-filters]')?.textContent || '{}');
        Object.entries(mediaFilters).forEach(([variantId, positions]) => this._mediaFilters.set(String(variantId), positions));
      } catch (error) {
        console.warn('[kaching-variant-bridge] Could not parse the variant data.', error);
        return;
      }

      if (this._variants.size === 0) {
        return;
      }

      this._debug = new URLSearchParams(window.location.search).has('kaching_bridge_debug');
      // Keyed by form id rather than by the element: an app that re-renders the form replaces the node, and the
      // selection has to survive that, otherwise the new node looks like a form we have never seen and the change
      // that came with it is swallowed as its initial state.
      this._selectedVariantIds = new Map();
      this._trackedForms = new WeakSet();
      this._observedInputs = new WeakSet();
      this._pendingForms = new Set();
      this._abortController = new AbortController();

      const { signal } = this._abortController;

      document.addEventListener('change', this._onInputEvent.bind(this), { capture: true, signal });
      document.addEventListener('input', this._onInputEvent.bind(this), { capture: true, signal });

      // Product forms can appear after load (quick buy drawer, section rendering), so keep looking for new ones.
      this._domObserver = new MutationObserver(() => this._scheduleScan());
      this._domObserver.observe(document.documentElement, { childList: true, subtree: true });

      this._pollId = window.setInterval(() => {
        if (!document.hidden) {
          this._scan();
        }
      }, POLL_INTERVAL);

      window.kachingVariantBridge = this;
      this._scan();

      this._log('ready', {
        variants: this._variants.size,
        mediaFilters: this._mediaFilters.size,
        forms: document.querySelectorAll(CART_FORM_SELECTOR).length
      });
    }

    disconnectedCallback() {
      this._abortController?.abort();
      this._domObserver?.disconnect();
      this._attributeObserver?.disconnect();
      window.clearInterval(this._pollId);
    }

    /**
     * Coalesce the DOM mutations an app like Kaching produces into a single scan per frame
     */
    _scheduleScan() {
      if (this._scanScheduled) {
        return;
      }

      this._scanScheduled = true;

      window.requestAnimationFrame(() => {
        this._scanScheduled = false;
        this._scan();
      });
    }

    /**
     * Pick up every product form on the page and reconcile it with the variant it currently points to
     */
    _scan() {
      document.querySelectorAll(CART_FORM_SELECTOR).forEach((form) => {
        const input = form.querySelector(VARIANT_INPUT_SELECTOR);

        if (!input) {
          return;
        }

        if (!this._trackedForms.has(form)) {
          this._trackedForms.add(form);
          form.addEventListener('variant:change', this._onVariantAnnounced.bind(this, form), {
            signal: this._abortController.signal
          });
        }

        const key = this._formKey(form);

        if (!this._selectedVariantIds.has(key)) {
          // First time we see this form: record what it points to without announcing anything.
          this._selectedVariantIds.set(key, String(input.value || ''));
          this._log('tracking form', { form: key, variant: input.value });
        }

        this._observeInput(form, input);
        this._sync(form, input);
      });
    }

    _formKey(form) {
      return form.getAttribute('id') || (form.dataset.kachingBridgeKey ||= `form-${(this._keyCounter = (this._keyCounter || 0) + 1)}`);
    }

    _log(message, detail) {
      if (this._debug) {
        console.log(`[kaching-variant-bridge] ${message}`, detail);
      }
    }

    /**
     * Make writes to the variant input observable, whichever way the app performs them
     */
    _observeInput(form, input) {
      if (this._observedInputs.has(input)) {
        return;
      }

      this._observedInputs.add(input);

      const scheduleSync = () => this._scheduleSync(form, input);

      if (nativeValueDescriptor?.set && nativeValueDescriptor?.get) {
        Object.defineProperty(input, 'value', {
          configurable: true,
          enumerable: true,
          get() {
            return nativeValueDescriptor.get.call(this);
          },
          set(value) {
            nativeValueDescriptor.set.call(this, value);
            scheduleSync();
          }
        });
      }

      this._attributeObserver ||= new MutationObserver((mutations) => {
        mutations.forEach((mutation) => this._scheduleSync(mutation.target.form, mutation.target));
      });

      this._attributeObserver.observe(input, { attributes: true, attributeFilter: ['value'] });
    }

    _onInputEvent(event) {
      const input = event.target;

      if (input instanceof HTMLInputElement && input.matches(VARIANT_INPUT_SELECTOR) && input.form) {
        this._scheduleSync(input.form, input);
      }
    }

    /**
     * Refresh which media the gallery hides for the variant that is about to be announced.
     *
     * Impact builds variant image sets from alt tags and keeps the media of the other option values hidden. That list is
     * normally refreshed by the section re-render the theme's own picker performs, and nothing re-renders when the
     * selection is made in an app, so the image to scroll to is still hidden. Selecting a hidden slide is not merely
     * ignored: the carousel resolves it to an index of -1 and throws, which leaves the gallery stuck. This applies the
     * same update the theme applies on re-render, using the theme's own filter events.
     */
    _refreshMediaFilter(form, variant) {
      const formId = form.getAttribute('id');
      const hiddenPositions = this._mediaFilters.get(String(variant.id));

      if (!formId || !hiddenPositions) {
        return;
      }

      const galleries = Array.from(document.querySelectorAll('product-gallery')).filter(
        (gallery) => gallery.getAttribute('form') === formId
      );

      // The theme stores the positions it hides one-based, but emits them zero-based to the carousels.
      const filteredIndexes = hiddenPositions.map((position) => position - 1);

      galleries.forEach((gallery) => {
        gallery.setAttribute('filtered-indexes', JSON.stringify(hiddenPositions));

        gallery.querySelectorAll('media-carousel').forEach((carousel) => {
          carousel.dispatchEvent(new CustomEvent('carousel:filter', { detail: { filteredIndexes } }));
        });

        gallery.querySelectorAll('page-dots').forEach((pageDots) => {
          pageDots.dispatchEvent(new CustomEvent('control:filter', { detail: { filteredIndexes } }));
        });
      });

      this._log('refreshed the gallery media filter', { form: formId, galleries: galleries.length, hidden: hiddenPositions });
    }

    /**
     * Someone else (the theme's own variant picker, or this bridge) already told the theme about a variant.
     *
     * Only an event the gallery can actually act on counts: an app that emits a "variant:change" of its own with a
     * payload the theme cannot read would otherwise silence this bridge without anything moving on screen.
     */
    _onVariantAnnounced(form, event) {
      const variant = event.detail?.variant;
      const usable = variant?.id !== undefined && variant?.id !== null && typeof variant?.featured_media?.position === 'number';

      this._log(usable ? 'variant:change from elsewhere, standing down' : 'variant:change from elsewhere is unusable, ignoring it', event.detail);

      if (usable) {
        this._selectedVariantIds.set(this._formKey(form), String(variant.id));
      }
    }

    /**
     * Defer to a microtask so that a picker which sets the value and then emits the event itself always wins
     */
    _scheduleSync(form, input) {
      if (!form || !input || this._pendingForms.has(form)) {
        return;
      }

      this._pendingForms.add(form);

      queueMicrotask(() => {
        this._pendingForms.delete(form);
        this._sync(form, input);
      });
    }

    _sync(form, input) {
      const key = this._formKey(form);
      const selectedVariantId = String(input.value || '');
      const knownVariantId = this._selectedVariantIds.get(key);

      if (selectedVariantId === knownVariantId) {
        return;
      }

      const variant = this._variants.get(selectedVariantId);

      if (!variant) {
        // Another product's form (a recommendation quick buy, for instance): remember it, but stay out of it.
        this._selectedVariantIds.set(key, selectedVariantId);
        this._log('form points at another product, ignoring it', { form: key, variant: selectedVariantId });
        return;
      }

      const previousVariant = this._variants.get(knownVariantId) || null;
      this._selectedVariantIds.set(key, selectedVariantId);

      this._log('emitting variant:change', {
        form: key,
        from: previousVariant?.title,
        to: variant.title,
        mediaPosition: variant.featured_media?.position
      });

      // Has to happen first: the gallery scrolls to the media as soon as it receives the event, and a media that is
      // still filtered out cannot be scrolled to.
      this._refreshMediaFilter(form, variant);

      form.dispatchEvent(
        new CustomEvent('variant:change', {
          bubbles: true,
          detail: {
            formId: form.getAttribute('id'),
            variant,
            previousVariant
          }
        })
      );
    }
  }

  window.customElements.define(ELEMENT_NAME, KachingVariantBridge);
})();
